import React, {
  useMemo,
  useRef,
  useState,
  useEffect,
  useCallback,
} from 'react';
import classNames from 'classnames';
import { Overlay, Portal } from 'ncoded-component-library';
import usePositioning from './hooks/usePositioning';
import { XOR } from 'types';

import './ResolvePopup.styles.scss';

type OpenOnHover = {
  onHover: true;
  customOnHover?: () => void;
};

type OpenOnState = {
  open: boolean;
};

export type ResolvePopupProps = {
  className?: string;
  portalClassName?: string;
  popupContent: React.ReactNode;
  contentClassName?: string;
  setRef?: React.MutableRefObject<HTMLElement>;
  style?: React.CSSProperties;
  preferUpwards?: boolean;
  closeOnScroll?: boolean;
  hasFadeEffect?: boolean;
  onCloseOnScroll?: () => void;
} & XOR<OpenOnHover, OpenOnState>;

const ResolvePopup: React.FC<ResolvePopupProps> = (props) => {
  const {
    children,
    className,
    popupContent,
    portalClassName,
    contentClassName,
    setRef,
    style,
    open,
    onHover,
    preferUpwards,
    closeOnScroll,
    hasFadeEffect = false,
    customOnHover,
    onCloseOnScroll,
  } = props;

  const [isUpwards, setIsUpwards] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const popupRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  usePositioning({
    triggerRef,
    contentRef,
    isOpen,
    preferUpwards,
    setIsUpwards,
  });

  const classes = classNames(
    'resolve-popup',
    { 'resolve-popup--open': isOpen, 'resolve-popup--upwards': isUpwards },
    className,
  );
  const portalClasses = classNames('resolve-popup__portal', portalClassName);
  const contentClasses = classNames(
    'resolve-popup__content',
    {
      'resolve-popup__content--open': isOpen,
      'resolve-popup__content--upwards': isUpwards,
      'resolve-popup__content--fade': hasFadeEffect,
    },
    contentClassName,
  );
  const triggerClasses = classNames(
    'resolve-popup__trigger',
    {
      'resolve-popup__trigger--open': isOpen,
      'resolve-popup__trigger--upwards': isUpwards,
    },
    contentClassName,
  );

  const content = useMemo(
    () => (
      <>
        {isOpen && (
          <Overlay
            open
            elevate
            parentEl={document.body}
            noContent
            background="transparent"
            className="resolve-popup__overlay"
          />
        )}
        <div className={contentClasses} ref={contentRef}>
          <div className="resolve-popup__content__content-wrapper">
            <div className="resolve-popup__content__content-wrapper__container">
              {popupContent}
            </div>
          </div>
        </div>
      </>
    ),
    [isOpen, contentClasses, popupContent],
  );

  const openOnMouse = useCallback(() => {
    if (!onHover) return;

    setIsOpen((prev) => {
      if (prev) return prev;

      customOnHover?.();
      return true;
    });
  }, [customOnHover, onHover]);

  useEffect(() => {
    if (!onHover) setIsOpen(open);
  }, [onHover, open]);

  useEffect(() => {
    if (!isOpen || !closeOnScroll) return;

    const close = (event: Event) => {
      const target = event.target as Node;

      if (target && contentRef.current?.contains(target)) return;

      setIsOpen(false);
      onCloseOnScroll?.();
    };

    window.addEventListener('scroll', close, true);
    return () => window.removeEventListener('scroll', close, true);
  }, [closeOnScroll, isOpen, onCloseOnScroll]);

  return (
    <div
      className={classes}
      style={style}
      onMouseOver={openOnMouse}
      onMouseLeave={() => onHover && setIsOpen(false)}
      ref={(r) => {
        popupRef.current = r;
        if (setRef) setRef.current = r;
      }}
    >
      <Portal className={portalClasses} isOpen={isOpen}>
        {content}
      </Portal>
      <div className={triggerClasses} ref={triggerRef}>
        {children}
      </div>
    </div>
  );
};

export default ResolvePopup;
